<template>
    <header class="step-header" :class="{ 'is-centred': !embedded && align === 'center', 'is-display': isDisplay }">
        <div class="titles">
            <div v-if="source || $slots.mark" class="title-row">
                <SetupCalendarIcon v-if="source === 'calendar'" class="calendar-mark" />
                <PerspectiveMark v-else-if="source === 'perspective'" class="perspective-mark" />
                <CollaborationProfileMark v-else-if="source === 'profile'" />
                <slot v-else name="mark"></slot>
                <component :is="embedded ? 'h2' : 'h1'" class="step-title" :class="titleType">{{ title }}</component>
            </div>
            <component :is="embedded ? 'h2' : 'h1'" v-else class="step-title" :class="titleType">{{ title }}</component>
            <p v-if="lede || $slots.default" class="lede" :class="ledeType">
                <slot>{{ lede }}</slot>
            </p>
            <!-- A div, not a p: the calendar step puts a <details> in here, and a block
                 inside a paragraph is auto-closed by the parser, which broke the gap. -->
            <div v-if="$slots.note" class="note">
                <slot name="note"></slot>
            </div>
        </div>
        <HorizontalDivider as="hr" spacing="none" />
    </header>
</template>

<script setup lang="ts">
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
/**
 * Default to section-heading size because the section rail already identifies the step. Reserve display
 * size for opening claims.
 */
import CollaborationProfileMark from "~vue/icons/CollaborationProfileMark.vue";
import PerspectiveMark from "~vue/icons/PerspectiveMark.vue";
import SetupCalendarIcon from "~vue/icons/SetupCalendarIcon.vue";
import { computed } from "vue";

const {
    align = "start",
    size = "step",
    embedded = false,
} = defineProps<{
    title: string;
    lede?: string;
    align?: "start" | "center";
    size?: "step" | "display";
    embedded?: boolean;
    source?: "calendar" | "perspective" | "profile";
}>();

const isDisplay = computed(() => !embedded && size === "display");
/* Bind one typography utility at a time; layering both makes the result depend on CSS emission order. */
const titleType = computed(() => (isDisplay.value ? "display-medium" : "title-subsection"));
const ledeType = computed(() => (isDisplay.value ? "body" : "body-small"));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .step-header {
        @apply flex flex-col gap-5;
    }

    .titles {
        @apply flex flex-col gap-1 text-left;
    }

    .title-row {
        @apply flex items-center gap-2.5;
    }

    .calendar-mark {
        @apply h-5 w-5.5;
    }

    .perspective-mark {
        @apply h-auto w-12.5;
    }

    /* Not `.title`: Tailwind generates a `title` utility of the same name, and the
       utility layer wins, which took this to 30px on desktop. */
    .step-title {
        @apply text-ink;
    }

    .lede {
        @apply text-balance text-ink-muted;
    }

    .note {
        @apply details mt-1 flex flex-wrap items-center gap-2 text-ink-muted;
    }

    /*
     * Limit the lede independently of the wider display title. Increase title-to-lede spacing; increase
     * divider spacing to prevent it from looking like the lede's underline.
     */
    .step-header.is-display {
        @apply gap-10;
    }

    .step-header.is-display .titles {
        @apply gap-5;
    }

    .step-header.is-display .lede {
        @apply mx-auto max-w-[56ch];
    }

    /* StepShell also uses is-centred, so the state must belong to this header. */
    .step-header.is-centred .titles {
        @apply items-center text-center;
    }

    .step-header.is-centred .title-row,
    .step-header.is-centred .note {
        @apply justify-center;
    }
}
</style>
